import { useState } from 'react'
import type { TmsInvoiceCreateRow, TmsWriteRow } from '@numeo/types-main-web-app'
import { Badge } from '@/components/ds2'
import { LOAD_STAGE_LABEL } from './format'
import { LoadFixActions, useFixSlots } from './LoadFixActions'
import { bolSlotOf } from './bolSlot'
import { useGeneralAgentChatStore } from '../../hooks/generalAgentChatStore'
import { LoadRow, documentSlotsOf } from './LoadRow'
import { byFixOrder, documentActions, REASON_LABEL, reasonActions } from './rowActions'
import { loadKeyOf } from './loadKey'
import { useUpdatedBelow } from './updatedBelow'
import { refusalTitleOf } from './refusalWords'

type BadgeType = 'success' | 'default' | 'warning' | 'danger'

const OUTCOME_BADGE: Record<TmsWriteRow['outcome'], { type: BadgeType; label: string }> = {
    updated: { type: 'success', label: 'Done' },
    unchanged: { type: 'default', label: 'Already so' },
    skipped: { type: 'warning', label: 'Skipped' },
    refused: { type: 'danger', label: 'Refused' },
}

/** The invoice card's words for its outcomes: a load it did not invoice is waiting on a fix, not refused for good. */
function badgeOf(card: string, row: TmsInvoiceCreateRow): { type: BadgeType; label: string } {
    if (card === 'tms_invoices.create') {
        if (row.outcome === 'updated') return { type: 'success', label: 'Invoiced' }
        if (row.code === 'already_invoiced') return { type: 'default', label: 'Already invoiced' }
        if (row.code === 'not_ready') return { type: 'warning', label: 'Not invoiced' }
    }
    return OUTCOME_BADGE[row.outcome] ?? { type: 'default', label: row.outcome }
}

/** A status change the card made, in the board's words ("In transit → Delivered"), for a row that carries no message of its own. */
const stageChange = (row: TmsWriteRow): string | undefined =>
    row.from_stage && row.to_stage && row.from_stage !== row.to_stage ? `${LOAD_STAGE_LABEL[row.from_stage]} → ${LOAD_STAGE_LABEL[row.to_stage]}` : undefined

/** A reason's message as one clause of the row's line. */
const clause = (message: string) => message.trim().replace(/\.$/, '')

interface WriteRowProps {
    card: string
    row: TmsInvoiceCreateRow
    onSend: (message: string) => void
}

/** One load a write card changed, on the shared load line, with the fixes a refusal or a delivered load still needs under it. */
const WriteRow = ({ card, row, onSend }: WriteRowProps) => {
    const badge = badgeOf(card, row)
    const dimmed = useUpdatedBelow(loadKeyOf(row))
    // In the order they are fixed, as the actions under them.
    const reasons = row.outcome === 'refused' ? byFixOrder(row.reasons ?? []) : []
    const delivered = row.outcome === 'updated' ? row.document_chips : undefined
    const fixes = reasons.length ? reasonActions(reasons) : documentActions(delivered)
    const [fixOpen, setFixOpen] = useState(false)
    const notify = useGeneralAgentChatStore((s) => s.notify)
    const slots = useFixSlots({ actions: fixes, loadNumber: row.load_number, loadId: row.load_id, onOpen: () => setFixOpen(true) })
    // A row's message is for the model: a refusal says its plain title, a change its stage move.
    const title = reasons.length
        ? reasons.map((reason) => REASON_LABEL[reason.code] ?? clause(reason.message)).join(' · ')
        : row.outcome === 'refused'
          ? refusalTitleOf(row)
          : (stageChange(row) ?? '')
    return (
        <li className="py-1.5 first:pt-0 last:pb-0">
            <LoadRow
                number={row.load_number}
                title={title}
                state={<Badge variant="soft" type={badge.type} size="sm" label={badge.label} />}
                documents={delivered ? documentSlotsOf(delivered) : undefined}
                dimmed={dimmed}
                action={
                    fixes.length > 0 ? (
                        <LoadFixActions
                            loadNumber={row.load_number}
                            loadId={row.load_id}
                            actions={fixes}
                            onSend={onSend}
                            open={fixOpen}
                            onOpenChange={setFixOpen}
                        />
                    ) : undefined
                }
                slots={{ ...slots, bol: delivered ? bolSlotOf({ loadNumber: row.load_number, loadId: row.load_id, onFiled: notify }) : undefined }}
            />
        </li>
    )
}

interface WriteRowsProps {
    /** The write card that answered, e.g. `tms_invoices.create`. */
    card: string
    rows: TmsWriteRow[]
    onSend: (message: string) => void
}

/**
 * A write card's answer: what happened to each load, in the card's words, on the shared load line. A load the invoice card left out
 * shows what it is waiting on and the fixes, right here (upload its rate con or POD, set its rate), so "invoice these loads" never ends
 * at a refusal. A load just marked delivered shows its documents and the uploads it still needs, the next step before its invoice.
 */
export const WriteRows = ({ card, rows, onSend }: WriteRowsProps) => (
    <ul className="flex flex-col text-xs">
        {rows.map((plain) => (
            <WriteRow key={plain.load_id} card={card} row={plain as TmsInvoiceCreateRow} onSend={onSend} />
        ))}
    </ul>
)
